import {
  Ban,
  Globe,
  Shuffle,
  Search,
  Layers,
  Lock,
  ChartColumn,
  Pencil,
} from 'lucide-react';

import { BoxSelectorOption } from '@@/BoxSelector';

import { PolicyConfigFormValues } from '../../PolicyConfigurationFormSection';

export interface KubernetesNetworkSecurityFormValues
  extends PolicyConfigFormValues, KubernetesNetworkSecurityTypeSpecificData {}

/**
 * Form data for Network Security Policy
 * These are the values displayed and edited in the form (UI representation)
 */
export interface KubernetesNetworkSecurityTypeSpecificData {
  namespaces: Namespace[];
  ingressRules: NetworkSecurityRule[];
  egressRules: NetworkSecurityRule[];
}

export type Protocol = '' | 'TCP' | 'UDP';

export interface NetworkSecurityRule {
  name?: string;
  // appliesTo defines which pods within the target namespaces a rule applies to.
  appliesTo?: PodSelection;
  // sourceType selects the peer type for this traffic rule.
  sourceType?: TrafficSourceType;
  // namespace is required for the namespace and namespace-pod-label source types.
  namespace?: string;
  // podLabels selects pods by label for the namespace-pod-label and pod-label-only source types.
  podLabels?: Record<string, string>;
  // CIDR is required for the cidr-block source type.
  cidr?: string;
  // port is the port number. Null means any port.
  port?: number;
  // protocol defaults to TCP when omitted.
  protocol?: Protocol;
}

// PodSelection defines which pods within the target namespaces a rule applies to.
export interface PodSelection {
  type: PodSelectionType;
  matchLabels?: Record<string, string>;
  matchExpressions?: MatchExpression[];
}

export interface Namespace {
  namespace: string;
}

export interface MatchLabel {
  key: string;
  value: string;
}

export interface MatchExpression {
  key: string;
  operator: ExpressionOperatorType;
  values: string[];
}

// TrafficSourceType defines the peer type for a traffic rule.
export enum TrafficSourceType {
  DenyAll = 'deny-all',
  Namespace = 'namespace',
  NamespacePodLabel = 'namespace-pod-label',
  PodLabelOnly = 'pod-label-only',
  CIDRBlock = 'cidr-block',
  Any = 'any',
}

export enum PodSelectionType {
  All = 'all',
  MatchLabels = 'matchLabels',
  MatchExpression = 'matchExpression',
}

export enum ExpressionOperatorType {
  In = 'In',
  NotIn = 'NotIn',
  Exists = 'Exists',
  DoesNotExist = 'DoesNotExist',
}

/**
 * Stored/API payload for Network Security Policy
 * This matches the actual backend API structure with nested objects
 */
export interface NetworkSecurityPolicyPayload {
  ingressRules: NetworkSecurityRulePayload[];
  egressRules: NetworkSecurityRulePayload[];
}

/**
 * Payload for NetworkSecurityRule.
 * Includes namespaces list in appliesTo field.
 */
export interface NetworkSecurityRulePayload {
  name?: string;
  // appliesTo defines which pods within the target namespaces a rule applies to.
  appliesTo?: PodSelectionPayload;
  // sourceType selects the peer type for this traffic rule.
  sourceType?: TrafficSourceType;
  // namespace is required for the namespace and namespace-pod-label source types.
  namespace?: string;
  // podLabels selects pods by label for the namespace-pod-label and pod-label-only source types.
  podLabels?: Record<string, string>;
  // CIDR is required for the cidr-block source type.
  cidr?: string;
  // port is the port number. Null means any port.
  port?: number;
  // protocol defaults to TCP when omitted.
  protocol?: Protocol;
}

/**
 * Payload for PodSelection.
 * Includes namespaces list.
 */
export interface PodSelectionPayload {
  namespaces: string[];
  type: PodSelectionType;
  matchLabels?: Record<string, string>;
  matchExpressions?: MatchExpression[];
}

/**
 * Default initial values for Network Security Policy type-specific fields (create mode)
 */
export const NETWORK_SECURITY_INITIAL_VALUES: KubernetesNetworkSecurityTypeSpecificData =
  {
    namespaces: [],
    ingressRules: [
      {
        name: 'deny-all',
        appliesTo: {
          type: PodSelectionType.All,
        },
        sourceType: TrafficSourceType.DenyAll,
      },
    ],
    egressRules: [],
  };

/**
 * Default initial values for complete Network Security Policy form (create mode)
 */
export const NETWORK_SECURITY_FORM_INITIAL_VALUES: KubernetesNetworkSecurityFormValues =
  {
    name: '',
    environmentGroupIds: [],
    ...NETWORK_SECURITY_INITIAL_VALUES,
  };

export const NETWORK_SECURITY_ADD_RULE_FORM_INITIAL_VALUES: NetworkSecurityAddRuleFormValues =
  {
    preset: undefined,
  };

export interface NetworkSecurityAddRuleFormValues extends NetworkSecurityRule {
  preset?: RulePresetType;
}

export enum RulePresetType {
  INGRESS_BLOCK_ALL_INBOUND = 'ingressBlockAllInbound',
  INGRESS_ALLOW_ALL_INBOUND = 'ingressAllowAllInbound',
  INGRESS_ALLOW_INGRESS_CONTROLLER = 'ingressAllowIngressController',
  INGRESS_ALLOW_FROM_NAMESPACE = 'ingressAllowFromNamespace',
  INGRESS_ALLOW_PROMETHEUS = 'ingressAllowPrometheus',
  EGRESS_BLOCK_ALL_OUTBOUND = 'egressBlockAllOutbound',
  EGRESS_ALLOW_ALL_OUTBOUND = 'egressAllowAllOutbound',
  EGRESS_ALLOW_DNS = 'egressAllowDNS',
  EGRESS_ALLOW_NAMESPACE = 'egressAllowNamespace',
  EGRESS_ALLOW_HTTPS = 'egressAllowHTTPS',
  CUSTOM = 'custom',
}

export const IngressRulePresetOptions: BoxSelectorOption<string>[] = [
  {
    id: 'blockAllInboundOption',
    value: RulePresetType.INGRESS_BLOCK_ALL_INBOUND,
    label: 'Block all inbound',
    description:
      'Drops all incoming traffic. Add allow rules below it to selectively open ports.',
    icon: Ban,
    iconType: 'badge',
  },
  {
    id: 'allowAllInboundOption',
    value: RulePresetType.INGRESS_ALLOW_ALL_INBOUND,
    label: 'Allow all inbound',
    description:
      'Permits all incoming traffic to pods. Use in dev environments or when auditing.',
    icon: Globe,
    iconType: 'badge',
  },
  {
    id: 'allowIngressControllerOption',
    value: RulePresetType.INGRESS_ALLOW_INGRESS_CONTROLLER,
    label: 'Allow from ingress controller',
    description:
      'Lets nginx (or similar) forward HTTP/S traffic into your pods.',
    icon: Shuffle,
    iconType: 'badge',
  },
  {
    id: 'allowFromNamespaceOption',
    value: RulePresetType.INGRESS_ALLOW_FROM_NAMESPACE,
    label: 'Allow from namespace',
    description: 'Permits traffic from all pods in a specific namespace.',
    icon: Layers,
    iconType: 'badge',
  },
  {
    id: 'allowPrometheusOption',
    value: RulePresetType.INGRESS_ALLOW_PROMETHEUS,
    label: 'Allow Prometheus',
    description: 'Opens port 9090 to Prometheus in the monitoring namespace.',
    icon: ChartColumn,
    iconType: 'badge',
  },
  {
    id: 'customOption',
    value: RulePresetType.CUSTOM,
    label: 'Custom',
    description:
      'Build a rule from scratch with full control over all options.',
    icon: Pencil,
    iconType: 'badge',
  },
];

export const EgressRulePresetOptions: BoxSelectorOption<string>[] = [
  {
    id: 'blockAllOutboundOption',
    value: RulePresetType.EGRESS_BLOCK_ALL_OUTBOUND,
    label: 'Block all outbound',
    description:
      'Drops all outgoing traffic. Add allow rules below it to selectively open destinations.',
    icon: Ban,
    iconType: 'badge',
  },
  {
    id: 'allowAllOutbountOption',
    value: RulePresetType.EGRESS_ALLOW_ALL_OUTBOUND,
    label: 'Allow all outbound',
    description:
      'Permits all outgoing traffic from pods. Use in dev environments or when auditing.',
    icon: Globe,
    iconType: 'badge',
  },
  {
    id: 'allowDNSOption',
    value: RulePresetType.EGRESS_ALLOW_DNS,
    label: 'Allow DNS',
    description:
      'Required for pods to resolve hostnames. Almost always needed when blocking outbound.',
    icon: Search,
    iconType: 'badge',
  },
  {
    id: 'allowToNamespaceOption',
    value: RulePresetType.EGRESS_ALLOW_NAMESPACE,
    label: 'Allow to namespace',
    description:
      'Permits outbound traffic to all pods in a specific namespace.',
    icon: Layers,
    iconType: 'badge',
  },
  {
    id: 'allowHTTPSOption',
    value: RulePresetType.EGRESS_ALLOW_HTTPS,
    label: 'Allow HTTPS outbound',
    description: 'Opens port 443 so pods can reach external APIs and services.',
    icon: Lock,
    iconType: 'badge',
  },
  {
    id: 'customOption',
    value: RulePresetType.CUSTOM,
    label: 'Custom',
    description:
      'Build a rule from scratch with full control over all options.',
    icon: Pencil,
    iconType: 'badge',
  },
];

export const PodSelectionOptions = [
  {
    label: 'All pods in namespace',
    value: PodSelectionType.All,
  },
  {
    label: 'Pods matching labels',
    value: PodSelectionType.MatchLabels,
  },
  {
    label: 'Pods matching expression',
    value: PodSelectionType.MatchExpression,
  },
];

export const ExpressionOperatorOptions = [
  {
    label: 'In',
    value: ExpressionOperatorType.In,
  },
  {
    label: 'NotIn',
    value: ExpressionOperatorType.NotIn,
  },
  {
    label: 'Exists',
    value: ExpressionOperatorType.Exists,
  },
  {
    label: 'DoesNotExist',
    value: ExpressionOperatorType.DoesNotExist,
  },
];

export const SourceTypeOptions = [
  {
    label: 'Deny all',
    value: TrafficSourceType.DenyAll,
  },
  {
    label: 'Namespace',
    value: TrafficSourceType.Namespace,
  },
  {
    label: 'Namespace pod label',
    value: TrafficSourceType.NamespacePodLabel,
  },
  {
    label: 'Pod label only',
    value: TrafficSourceType.PodLabelOnly,
  },
  {
    label: 'CIDR block',
    value: TrafficSourceType.CIDRBlock,
  },
  {
    label: 'Any',
    value: TrafficSourceType.Any,
  },
];

export const ProtocolOptions = [
  {
    label: 'TCP',
    value: 'TCP' as Protocol,
  },
  {
    label: 'UDP',
    value: 'UDP' as Protocol,
  },
];

export function buildLabelsItem(): MatchLabel {
  return { key: '', value: '' };
}
